import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { ContentAlign } from "@app/components/base-page/enums";
import {
  BlockHeader,
  HeaderLevel,
} from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { Instagram } from "@app/components/common/icons";
import { IAmbassador } from "@app/models/storyblok/blocks/ambassador";
import { DEFAULT_FADE_DURATION } from "@app/constants";

type Props = {
  ambassadorsList: IAmbassador[];
};

export const List: FC<Props> = ({ ambassadorsList }) => (
  <div
    className={classNames(
      "w-full space-y-20 text-primary-content",
      "lg:space-y-25",
      "xl:space-y-40",
    )}
  >
    {ambassadorsList.map((ambassador) => (
      <Fade key={ambassador._uid} duration={DEFAULT_FADE_DURATION} triggerOnce>
        <div
          className={classNames("px-5", "lg:px-16", "xl:px-24", {
            "bg-dark py-10 lg:py-15 xl:py-30": ambassador.isBackground,
          })}
        >
          <div className="xxl:mx-auto xxl:max-w-container">
            <div
              className={classNames("md:w-2/3", {
                "md:mr-auto": ambassador.contentSide === ContentAlign.LEFT,
                "md:ml-auto": ambassador.contentSide === ContentAlign.RIGHT,
              })}
            >
              <GatsbyImage
                image={getGatsbyImage(ambassador.image.filename, {
                  layout: "fullWidth",
                })}
                alt={ambassador.image.alt}
                className={classNames(
                  "mb-5 w-full rounded-lg",
                  "md:mb-10",
                  "xxl:mb-15",
                )}
              />
              <div className={classNames("md:w-[73%]", "lg:w-full")}>
                <BlockHeader
                  richText={ambassador.title}
                  headerLevel={HeaderLevel.H2}
                  className={classNames(
                    "text-medium",
                    "lg:text-large",
                    "xxl:text-xx-large",
                  )}
                />
              </div>
              <div
                className={classNames(
                  "mt-2.5 text-regular",
                  "md:mt-5",
                  "lg:text-medium",
                  "xxl:text-large",
                )}
              >
                <BlockBody richText={ambassador.text} />
              </div>

              <a
                href={ambassador.instagramLink}
                className={classNames(
                  "mt-2.5 flex items-center gap-2.5 text-regular no-underline",
                  "md:mt-5",
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram />
                <span>{ambassador.instagramLabel}</span>
              </a>
            </div>
          </div>
        </div>
      </Fade>
    ))}
  </div>
);
